import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { cn } from "../../lib/utils";

interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface AlertDialogContentProps {
  className?: string;
  children: React.ReactNode;
}

interface AlertDialogActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

interface AlertDialogCancelProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

const AlertDialog: React.FC<AlertDialogProps> = ({ open, onOpenChange, children }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog>
  );
};

const AlertDialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, ...props }, ref) => (
  <button ref={ref} {...props}>
    {children}
  </button>
));
AlertDialogTrigger.displayName = "AlertDialogTrigger";

const AlertDialogContent: React.FC<AlertDialogContentProps> = ({ className, children, ...props }) => {
  return (
    <DialogContent className={cn("sm:max-w-[425px]", className)} {...props}>
      {children}
    </DialogContent>
  );
};

const AlertDialogHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ 
  className, 
  children, 
  ...props 
}) => {
  return (
    <DialogHeader className={className} {...props}>
      {children}
    </DialogHeader>
  );
};

const AlertDialogFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ 
  className, 
  children, 
  ...props 
}) => {
  return (
    <DialogFooter className={className} {...props}>
      {children}
    </DialogFooter>
  );
};

const AlertDialogTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ 
  className, 
  children, 
  ...props 
}) => {
  return (
    <DialogTitle className={className} {...props}>
      {children}
    </DialogTitle>
  );
};

const AlertDialogDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ 
  className, 
  children, 
  ...props 
}) => {
  return (
    <DialogDescription className={className} {...props}>
      {children}
    </DialogDescription>
  );
};

const AlertDialogAction: React.FC<AlertDialogActionProps> = ({ 
  className, 
  children, 
  onClick,
  ...props 
}) => {
  return (
    <Button 
      className={cn("bg-red-500 hover:bg-red-600 text-white", className)} 
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};

const AlertDialogCancel: React.FC<AlertDialogCancelProps> = ({ 
  className, 
  children, 
  onClick,
  ...props 
}) => {
  return (
    <Button 
      variant="outline" 
      className={className} 
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}; 